/** Auto-generated English translations for src/data/complaints.ts */
import { complaints, type Complaint } from "./complaints";
import { deepSections } from "./complaints.deep";


type ComplaintEn = Omit<Complaint, "slug" | "relatedPosts" | "serviceAnchor">;

export const complaintsEn: Record<string, ComplaintEn> = {
  "reizdarm-blaehbauch": {
    "title": "Irritable Bowel Syndrome, Bloating and Persistent Digestive Complaints",
    "shortTitle": "IBS & Bloating",
    "metaTitle": "IBS & Bloating: Find the Cause | PRAXIS IB Vienna",
    "metaDescription": "IBS, bloating, cramps, altered bowel habits: microbiological diagnostics and individualised complementary medicine support in Vienna.",
    "keywords": [
      "IBS Vienna",
      "causes of bloating",
      "irritable bowel syndrome treatment",
      "complementary medicine digestive issues",
      "microbiome analysis Vienna"
    ],
    "synonyms": [
      "Irritable bowel syndrome (IBS)",
      "bloated stomach after eating",
      "abdominal cramps with normal test results",
      "alternating diarrhoea and constipation",
      "developing food intolerances"
    ],
    "intro": "Irritable bowel syndrome is not in your head, nor is it a second-class diagnosis. It describes a condition in which gut motility, the mucosa, the microbiota and the nervous system no longer interact effectively. That is exactly why asking \"What is abnormal in the test results?\" is often less helpful than asking \"Which regulatory systems are out of sync?\".",
    "sections": [
      {
        "heading": "Why normal test results do not invalidate your symptoms",
        "paragraphs": [
          "A colonoscopy, blood count and ultrasound rule out structural diseases – this is important and always the first step. However, they reflect neither the composition of the gut flora, nor the barrier function of the mucous membrane, nor the activity of the gut-associated immune system.",
          "There is a vast functional space between being \"organically healthy\" and \"symptom-free\". This is precisely where complementary medicine operates: not as a substitute for conventional medicine, but as a supplement where standard diagnostics fail to provide an answer."
        ]
      },
      {
        "heading": "The potential microbiological background",
        "paragraphs": [
          "A reduced diversity of the gut flora, a deficiency in mucosa-protecting bacteria such as Akkermansia muciniphila or Faecalibacterium prausnitzii, small intestinal bacterial overgrowth (SIBO), impaired bile acid or carbohydrate metabolism, as well as the aftermath of antibiotic therapies are frequent contributing factors.",
          "Short-chain fatty acids – primarily butyrate – are the main energy source for the colonic mucosa. If the bacteria that produce them are missing, the barrier suffers, and the gut becomes more sensitive to distension, gas production and stress."
        ]
      },
      {
        "heading": "Taking the gut-brain axis seriously",
        "paragraphs": [
          "The gut and brain communicate continuously via the vagus nerve, immune messengers and microbial metabolites. Chronic stress measurably alters motility, blood flow and pain perception – and an irritated gut, in turn, affects your mood and resilience. Both directions must be taken into consideration."
        ]
      },
      {
        "heading": "What is realistically achievable",
        "paragraphs": [
          "Experience shows that IBS is one of those conditions where consistent effort can bring about significant improvements. In the vast majority of cases, patients report a marked reduction in bloating, calmer digestion, a broader range of tolerated foods – and, above all, that they no longer have to plan their day around their gut after a few months.",
          "Being symptom-free for extended periods is not an exception, but a realistic goal. Even if sensitive phases may still occur, you will learn to understand them and take early countermeasures. This is the difference between feeling helpless and being in control – and it is almost always achievable."
        ]
      }
    ],
    "signs": [
      "Bloating that worsens throughout the day",
      "Cramps, a feeling of pressure or fullness after eating",
      "Alternating bowel habits without a discernible pattern",
      "Increasing intolerance to specific foods",
      "Symptoms that worsen significantly under stress"
    ],
    "approach": [
      "Comprehensive medical history, including previous test results, medication and the course of the condition",
      "Microbiological stool diagnostics via specialist laboratories (including Enterosan and MicroGenDX)",
      "Assessment of barrier, inflammatory and digestive markers",
      "Individualised treatment plan incorporating micronutrients, phytotherapeutics and a nutritional strategy",
      "Step-by-step adjustments based on follow-up evaluations"
    ],
    "faq": [
      {
        "q": "Is IBS curable?",
        "a": "IBS is a functional disorder, not an unchangeable fate. If the contributing factors – microbiota, mucosa, diet, stress axis – are addressed specifically, symptoms improve significantly in many cases. However, there is no guarantee of a cure."
      },
      {
        "q": "Do I need a stool analysis?",
        "a": "Not always. It is useful if your symptoms persist, if multiple dietary changes have been unsuccessful, or if your medical history and symptoms point to dysbiosis. I will decide which analysis is appropriate following your consultation."
      },
      {
        "q": "Do I have to eliminate foods permanently?",
        "a": "Restrictive diets can provide short-term relief, but in the long term, they reduce the diversity of your gut flora. The goal is therefore always to rebuild your tolerance rather than permanently restrict your diet."
      }
    ]
  },
  "sibo-duenndarmfehlbesiedlung": {
    "title": "SIBO – Recognising and treating small intestinal bacterial overgrowth",
    "shortTitle": "SIBO / Small Intestinal Bacterial Overgrowth",
    "metaTitle": "SIBO: Diagnosis & Treatment | Praxis IB Vienna",
    "metaDescription": "Understanding SIBO: breath tests, hydrogen, methane, relapse risk and what matters after eradication. Complementary medical support in Vienna.",
    "keywords": [
      "SIBO Vienna",
      "small intestinal bacterial overgrowth symptoms",
      "SIBO breath test",
      "methane SIBO constipation",
      "SIBO relapse"
    ],
    "synonyms": [
      "Bloating immediately after eating",
      "Abdomen becomes increasingly bloated throughout the day",
      "Improvement with antibiotics, followed by a relapse",
      "Intolerance to fibre and probiotics"
    ],
    "intro": "In small intestinal bacterial overgrowth, bacteria colonise an area where they do not belong in such high numbers: the upper small intestine. They ferment carbohydrates before the body can absorb them, resulting in gas production, pressure and often nutrient deficiencies.",
    "sections": [
      {
        "heading": "Why SIBO comes back so frequently",
        "paragraphs": [
          "Antibiotic or herbal eradication therapies reduce the bacterial load, but they do not automatically address the root cause. If sluggish small intestinal motility, a lack of stomach acid or bile acids, adhesions or an underlying condition are left unaddressed, the overgrowth will usually return.",
          "Therefore, every SIBO treatment must include a second phase: restoring motility, digestive function and mucosal protection."
        ]
      },
      {
        "heading": "Hydrogen, methane and hydrogen sulphide",
        "paragraphs": [
          "Hydrogen-dominant forms are more frequently associated with diarrhoea, whereas methane-dominant forms (correctly termed: intestinal methanogen overgrowth) tend to present with constipation and severe bloating. Distinguishing between them significantly alters the choice of therapy – there is no one-size-fits-all 'SIBO protocol'."
        ]
      },
      {
        "heading": "The outlook for recovery",
        "paragraphs": [
          "SIBO has a reputation for being stubborn – and rightly so, if only the bacteria are reduced while the root cause is overlooked. However, if both are addressed, the outlook is much more positive: tackling motility, bile acids, digestive function and dietary patterns together leads to lasting symptom relief in many cases.",
          "Relapses are possible, but they do not mean the treatment has failed; rather, they usually indicate that an underlying factor has not yet been resolved. With a patient, step-by-step approach, most people achieve a very good quality of life and are able to enjoy a broad, relaxed diet once again."
        ]
      }
    ],
    "signs": [
      "Bloating within 30 to 90 minutes of a meal",
      "Worsening of symptoms with fibre, prebiotics or probiotics",
      "Unexplained iron or vitamin B12 deficiency",
      "Short-term improvement with antibiotics, followed by a relapse"
    ],
    "approach": [
      "Evaluation of existing breath tests and previous medical records",
      "Differentiation between hydrogen- and methane-dominant cases",
      "Phytotherapeutic strategies and targeted micronutrients",
      "Restoring motility, digestive function and the mucosal barrier",
      "Relapse prevention with a realistic dietary framework"
    ],
    "faq": [
      {
        "q": "Is a breath test absolutely necessary?",
        "a": "It is helpful, but not infallible. False-negative results can occur. Medical history, symptom patterns and clinical progress are just as informative and are evaluated together."
      },
      {
        "q": "How long does the treatment process take?",
        "a": "Realistically, it takes several months, as the reduction phase must be followed by a restoration phase. Experience shows that short-term protocols on their own often lead to relapses."
      }
    ]
  },
  "chronische-blasenentzuendung": {
    "title": "Chronic Cystitis, Interstitial Cystitis and Pelvic Pain",
    "shortTitle": "Bladder & Pelvic Pain",
    "metaTitle": "Chronic & Interstitial Cystitis | Praxis IB Vienna",
    "metaDescription": "Recurrent UTIs, interstitial cystitis and CPPS: biofilms, the urinary microbiome and advanced DNA diagnostics explained clearly.",
    "keywords": [
      "chronic cystitis Vienna",
      "interstitial cystitis",
      "painful bladder syndrome",
      "recurrent urinary tract infections",
      "CPPS pelvic pain"
    ],
    "synonyms": [
      "cystitis keeps coming back",
      "negative urine culture, symptoms persist",
      "constant urge to urinate without infection",
      "burning and pressure in the lower abdomen"
    ],
    "intro": "When bladder symptoms keep returning or become constant despite a clear urine culture, the medical explanation often ends for many patients – but the suffering does not. This is exactly where it is worth looking at biofilms, the urinary microbiome and the mucosal barrier.",
    "sections": [
      {
        "heading": "Why the standard urine culture often finds nothing",
        "paragraphs": [
          "The standard culture is designed to detect a small number of fast-growing pathogens. Bacteria that persist in biofilms or intracellularly within the bladder wall largely evade it. A negative result therefore does not automatically mean that there is no microbial involvement.",
          "DNA-based methods such as qPCR and next-generation sequencing can detect significantly more species – including anaerobic and hard-to-culture bacteria."
        ]
      },
      {
        "heading": "Biofilms as a cause of relapse",
        "paragraphs": [
          "Within a biofilm, bacteria are surrounded by a protective matrix, communicate via quorum sensing and are significantly less susceptible to antibiotics. If an infection initially subsides with treatment only to return weeks later, this is a typical pattern."
        ]
      },
      {
        "heading": "When no pathogen is involved",
        "paragraphs": [
          "In cases of interstitial cystitis and chronic pelvic pain syndrome (CPPS), the focus lies on a damaged glycosaminoglycan layer, mast cell activation, muscular tension in the pelvic floor and sensitised pain processing. These areas can also be specifically supported."
        ]
      },
      {
        "heading": "Why I am confident about this",
        "paragraphs": [
          "Particularly with recurrent bladder and lower abdominal symptoms, I repeatedly see recoveries that no one would have previously thought possible: longer infection-free intervals, fewer antibiotics, significantly less pain and urgency – and eventually months where the bladder is simply no longer an issue.",
          "If biofilms, the microbiome, the mucosal barrier and the inflammatory load are addressed systematically, long-term symptom relief is realistic in many cases. This applies even to patients with a long medical history. Please do not let the duration of your condition deter you from taking a structured approach once more."
        ]
      }
    ],
    "signs": [
      "more than three urinary tract infections per year",
      "symptoms despite a negative urine culture",
      "burning, pressure or pain in the lower abdomen and pelvic floor",
      "frequent urge to urinate, especially at night",
      "worsening of symptoms due to stress, the menstrual cycle or certain foods"
    ],
    "approach": [
      "systematic review of previous test results and antibiotic history",
      "advanced microbiological diagnostics, including DNA-based methods",
      "phytotherapeutic approaches targeting biofilms",
      "restoration of mucosal protection and urogenital flora",
      "support for the pain and stress axis"
    ],
    "faq": [
      {
        "q": "Does this replace urological treatment?",
        "a": "No. Urological assessment remains the foundation. My work is complementary, focusing on cases where relapses persist despite guideline-compliant therapy."
      },
      {
        "q": "How long does it take to see results?",
        "a": "In chronic cases, initial improvements are usually noticeable after eight to twelve weeks. Biofilm-associated conditions take longer and require a patient, consistent plan."
      }
    ]
  },
  "haut-akne-ekzeme": {
    "title": "Skin Problems: Acne, Eczema and the Gut-Skin Axis",
    "shortTitle": "Skin & the Gut-Skin Axis",
    "metaTitle": "Acne, Eczema & Gut-Skin Axis Causes | Praxis IB Vienna",
    "metaDescription": "Why skin issues often originate in the gut: the gut-skin axis, microbiome, sebum regulation and inflammation. Complementary medical support in Vienna.",
    "keywords": [
      "gut-skin axis",
      "acne gut microbiome",
      "eczema atopic dermatitis gut",
      "rosacea complementary",
      "skin problem causes Vienna"
    ],
    "synonyms": [
      "breakouts despite skincare",
      "atopic eczema flare-ups",
      "rosacea and redness",
      "skin reacting to stress and diet"
    ],
    "intro": "The skin and gut share a barrier function, immune cells and their own respective microbiomes. This is why the skin is often the visible endpoint of a process that begins deeper inside.",
    "sections": [
      {
        "heading": "How the gut-skin axis works",
        "paragraphs": [
          "Short-chain fatty acids produced by the gut flora regulate inflammation and influence the maturation of T cells. If they are lacking, the immune environment shifts towards inflammation – and the skin reacts with redness, flare-ups or delayed healing.",
          "At the same time, blood sugar fluctuations, insulin and IGF-1 influence sebum production. Diet therefore affects the skin not through myths, but via comprehensible signalling pathways."
        ]
      },
      {
        "heading": "After antibiotics or isotretinoin",
        "paragraphs": [
          "Many chronic skin conditions have been treated with antibiotics for years. This leaves traces in the gut flora that do not correct themselves. A targeted restoration is therefore often the crucial step to prevent relapses."
        ]
      },
      {
        "heading": "What you can expect",
        "paragraphs": [
          "Skin takes time because it renews itself in cycles – but that is exactly why it is such a responsive organ: what becomes calmer inside is reflected on the outside. Experience shows that most people see fewer inflamed areas, milder flare-ups and a significantly more resilient skin barrier after two to three months.",
          "Longer symptom-free periods are achievable in many cases. And with them returns something that skin diseases often take away so completely: the unselfconscious confidence to simply show yourself."
        ]
      }
    ],
    "signs": [
      "Skin complaints alongside digestive issues",
      "Flare-ups following stress, sugar or dairy products",
      "Recurring symptoms after antibiotic therapy",
      "Dry, sensitive skin with an impaired barrier"
    ],
    "approach": [
      "Combined assessment of the skin, gut and immune system",
      "Microbiological diagnostics for accompanying digestive complaints",
      "Micronutrients for the skin barrier and wound healing (e.g. zinc, vitamin A, omega-3)",
      "Phytotherapeutic support for inflammatory processes",
      "Gentle, practical dietary adjustments without lists of forbidden foods"
    ],
    "faq": [
      {
        "q": "Is acne really a gut issue?",
        "a": "Not exclusively. Hormones, genetics and sebaceous gland activity all play a role. However, the gut is a factor that can be easily influenced – and is often the overlooked one."
      },
      {
        "q": "How long does it take to see changes?",
        "a": "Skin renewal takes time. Two to three months is a realistic timeframe before a stable trend emerges."
      }
    ]
  },
  "histaminintoleranz-unvertraeglichkeiten": {
    "title": "Histamine Intolerance and Food Intolerances",
    "shortTitle": "Histamine & Intolerances",
    "metaTitle": "Histamine & Food Intolerances | PRAXIS IB Vienna",
    "metaDescription": "Histamine, DAO, fructose and lactose intolerance: why intolerances are often the result, not the cause. Complementary medical support in Vienna.",
    "keywords": [
      "Histamine intolerance Vienna",
      "DAO deficiency",
      "Fructose intolerance",
      "Food intolerance test",
      "Mast cell activation"
    ],
    "synonyms": [
      "Flushing, palpitations or headaches after eating",
      "Intolerance to an increasing number of foods",
      "Reactions to wine, cheese, tomatoes",
      "Itching or hives without a proven allergy"
    ],
    "intro": "If you find yourself having to eliminate more and more foods from your diet, the problem is rarely the food itself. Usually, your tolerance has decreased—due to an impaired mucosal barrier, an altered microbiota, or an overstimulated immune system.",
    "sections": [
      {
        "heading": "Histamine is about balance, not just ingredients",
        "paragraphs": [
          "The crucial factor is the ratio between histamine intake, the body's own release from mast cells, and the breakdown capacity via diamine oxidase (DAO) and HNMT. Histamine-producing bacteria in the gut also contribute to the overall load.",
          "This is why the same meal might be well tolerated one day but not the next—depending on stress levels, your menstrual cycle, sleep quality, and any underlying infections."
        ]
      },
      {
        "heading": "Why elimination diets alone are rarely enough",
        "paragraphs": [
          "Avoidance provides relief and is useful as an interim step. However, if it remains the only measure, the diversity of your gut flora will continue to decline, and your tolerance will decrease in the long term. The goal is therefore a gradual restoration."
        ]
      },
      {
        "heading": "Tolerance can return",
        "paragraphs": [
          "Intolerances feel permanent, but they rarely are. Because histamine breakdown, mucosal health, the microbiota, and micronutrient status are closely linked, tolerance very often improves as soon as these foundations are restored.",
          "The explicit goal is not a perfectly managed list of foods to avoid, but an everyday life where you can eat out, travel, and be spontaneous again. In most cases, exactly this is achievable—often with significantly more flexibility than initially thought possible."
        ]
      }
    ],
    "signs": [
      "Symptoms 20 to 60 minutes after eating",
      "Headaches, flushing, palpitations or a runny nose without a cold",
      "A growing list of foods you cannot tolerate",
      "Concurrent digestive issues"
    ],
    "approach": [
      "Symptom and food diaries instead of blanket diagnostic tests",
      "Assessment of the gut barrier, microbiota and inflammatory markers",
      "Targeted micronutrients for breakdown pathways and mast cell stability",
      "Structured restoration of tolerance"
    ],
    "faq": [
      {
        "q": "Are IgG food intolerance tests useful?",
        "a": "Not as the sole basis for making a decision. IgG antibodies primarily indicate contact with a food, not an intolerance to it. Clinical progress, food diaries, and targeted provocations are much more meaningful."
      },
      {
        "q": "Do I have to avoid histamine permanently?",
        "a": "In most cases, no. Once the gut barrier and breakdown capacity are functioning better, the amount you can tolerate usually increases noticeably."
      }
    ]
  },
  "wiederkehrende-infekte-immunsystem": {
    "title": "Recurrent Infections and an Imbalanced Immune System",
    "shortTitle": "Susceptibility to Infection & the Immune System",
    "metaTitle": "Always Ill? Immune System & Infections | Praxis IB Vienna",
    "metaDescription": "Recurrent infections, prolonged recovery, silent inflammation: lymphocyte subtyping, the microbiome and micronutrients explained – Praxis IB Vienna.",
    "keywords": [
      "susceptibility to infection Vienna",
      "always ill immune system",
      "lymphocyte subtyping",
      "strengthen immune system microbiome",
      "silent inflammation"
    ],
    "synonyms": [
      "every infection lasts for weeks",
      "constantly catching colds",
      "recurrent herpes or fungal infections",
      "immune system weakened after antibiotics"
    ],
    "intro": "An immune system that frequently gives in is usually not 'too weak', but rather poorly regulated. Around 70 per cent of immune cells are located in the gut-associated lymphoid tissue – this is where a large part of the immune response is determined.",
    "sections": [
      {
        "heading": "Regulation rather than boosting",
        "paragraphs": [
          "The idea of 'boosting' the immune system falls short. What matters more is the balance between immune readiness and tolerance. Regulatory T cells, which are heavily influenced by microbial metabolites, play a central role in this process."
        ]
      },
      {
        "heading": "What lymphocyte subtyping can reveal",
        "paragraphs": [
          "It shows the distribution of T helper cells, cytotoxic T cells, B cells and NK cells. In cases of persistent susceptibility to infection, following severe infections, or when chronic reactivations are suspected, it provides significantly more information than a differential blood count alone."
        ]
      },
      {
        "heading": "The immune system can be restabilised",
        "paragraphs": [
          "The immune system is not a fixed state, but a learning system capable of regeneration. When barrier function, the microbiota, micronutrients, sleep and the stress axis are addressed collectively, infection-free intervals typically increase significantly – often within the course of a single year.",
          "After a few months, many people report shorter and milder infections, a reduced need for antibiotics and a noticeable return of resilience. This outcome is the norm, not a stroke of luck."
        ]
      }
    ],
    "signs": [
      "more than four to six infections per year",
      "infections that repeatedly spread to the chest",
      "prolonged recovery periods after every infection",
      "recurrent reactivations (e.g. herpes labialis)",
      "weakened immunity following multiple courses of antibiotics"
    ],
    "approach": [
      "medical history of infection patterns and previous treatments",
      "targeted immune diagnostics, when clinically actionable",
      "building mucosal immunity via the microbiota",
      "micronutrients with proven immunological relevance (vitamin D, zinc, selenium, vitamin A)",
      "consideration of the stress axis as an immune modulator"
    ],
    "faq": [
      {
        "q": "How many infections per year are normal?",
        "a": "For adults, two to four respiratory infections per year are considered typical. Significantly more, very prolonged courses or frequent complications warrant further investigation."
      },
      {
        "q": "Do probiotics help?",
        "a": "When strain-specific and used in a targeted manner, they can be helpful. Taking a random supplement without prior testing, however, is usually of little benefit."
      }
    ]
  },
  "stress-hormone-schlaf": {
    "title": "Chronic Stress, Hormone Balance and Poor Sleep",
    "shortTitle": "Stress, Hormones & Sleep",
    "metaTitle": "Stress, Hormones & Sleep: HPA Axis | Praxis IB Vienna",
    "metaDescription": "How the stress axis, neurotransmitters and microbiome interact – and why sleep problems, inner restlessness and exhaustion often share the same root.",
    "keywords": [
      "effects of chronic stress",
      "HPA axis cortisol",
      "causes of sleep problems",
      "psychoneuroimmunology Vienna",
      "inner restlessness complementary"
    ],
    "synonyms": [
      "waking up between 2 and 4 am",
      "inner restlessness despite exhaustion",
      "exhaustion after prolonged stress",
      "irritability and concentration problems"
    ],
    "intro": "Persistent stress is not just a feeling, but a biochemical state. It alters the cortisol rhythm, immune response, digestion and sleep architecture, thereby directly contributing to physical symptoms.",
    "sections": [
      {
        "heading": "What Psychoneuroimmunology Describes",
        "paragraphs": [
          "PNI investigates how the psyche, nervous system, endocrine system and immune system communicate with one another. These systems do not work in isolation; instead, they constantly exchange signals via chemical messengers – through cytokines, hormones and neurotransmitters.",
          "In this context, mitochondria are more than just energy providers: they release signals that influence immune cells and neurotransmitter synthesis. Disruptions in this signalling process affect inflammation regulation, cellular metabolism and pain perception."
        ]
      },
      {
        "heading": "The Gut as a Site of Neurotransmitter Production",
        "paragraphs": [
          "A significant portion of serotonin production takes place in the gut, and microbial metabolites influence GABA and dopamine pathways. An imbalanced microbiota can therefore have a noticeable impact on sleep, motivation and stress resilience."
        ]
      },
      {
        "heading": "Regulation Can Be Restored",
        "paragraphs": [
          "Hormonal and autonomic control loops are designed to recover. This is precisely why improvements in this area are often impressive: more restful sleep, more stable energy levels throughout the day, a more balanced mood and a menstrual cycle that regains its rhythm – usually within just a few months.",
          "The same applies to thyroid and adrenal issues: even if an underlying condition remains, a very good quality of life is almost always achievable. To do so, the body primarily requires reliable conditions – and time."
        ]
      }
    ],
    "signs": [
      "Falling asleep is easy, staying asleep is not",
      "Exhausted in the morning, wired in the evening",
      "Digestive issues under stress",
      "Increased susceptibility to infections during stressful periods",
      "Muscle tension, teeth grinding, inner restlessness"
    ],
    "approach": [
      "Evaluating daily rhythm, stress levels and regeneration",
      "Targeted diagnostics of cortisol patterns and relevant micronutrients",
      "Magnesium, B vitamin and amino acid status as the foundation for neurotransmitter production",
      "Adaptogenic phytotherapy based on individual constitution",
      "Microbiome support to strengthen the gut-brain axis"
    ],
    "faq": [
      {
        "q": "Is this psychotherapy?",
        "a": "No. I work on the physical side of stress regulation. Psychotherapeutic support is a separate, often highly beneficial complement."
      },
      {
        "q": "Can a daily cortisol profile help?",
        "a": "If symptoms and daily energy patterns do not match, it can provide valuable clues. However, it does not replace an endocrinological assessment if an adrenal gland disorder is suspected."
      }
    ]
  },
  "urtikaria-nesselsucht": {
    "title": "Chronic Urticaria: When your skin sounds a daily alarm",
    "shortTitle": "Urticaria & Hives",
    "metaTitle": "Chronic Urticaria & Hives: Causes | Praxis IB Vienna",
    "metaDescription": "Chronic spontaneous urticaria: understanding mast cell activation, histamine metabolism, the microbiome and infection foci. Support in Vienna.",
    "keywords": [
      "chronic urticaria",
      "causes of hives",
      "hives without triggers",
      "mast cell activation",
      "urticaria Vienna complementary medicine"
    ],
    "synonyms": [
      "Hives",
      "Wheals all over the body",
      "Itching without an obvious trigger",
      "Chronic spontaneous urticaria (CSU)",
      "Angioedema / swelling of the lips and eyelids"
    ],
    "intro": "Few conditions are as profoundly exhausting as chronic urticaria. The hives appear, move around, disappear – leaving you wondering why no one can find a trigger. Medically speaking, this is entirely typical. In the majority of chronic spontaneous urticaria cases, there is no single allergen. This is not because there is nothing there, but because the root cause lies not in an external trigger, but in a lowered activation threshold of the mast cell.",
    "sections": [
      {
        "heading": "What actually happens in the skin",
        "paragraphs": [
          "A wheal forms through the degranulation of cutaneous mast cells. This releases histamine, tryptase, leukotrienes and prostaglandin D2, resulting in vasodilation, plasma leakage into the dermis and the activation of sensory C-fibres – leading to redness, swelling and itching. This explains why antihistamines reliably provide relief but do not change the underlying cause: they block the H1 receptor, but they do not stabilise the cell releasing the messenger substance.",
          "A significant proportion of chronic spontaneous urticaria cases involve an autoimmune component: IgG autoantibodies against the high-affinity IgE receptor (FcεRI) or against IgE itself (Type IIb), or IgE autoantibodies directed against the body's own structures, such as thyroperoxidase (Type I). This is why urticaria that has persisted for months is not an allergy in the traditional sense, but rather a state of chronic immune activation."
        ]
      },
      {
        "heading": "Why the search for a single trigger usually fails",
        "paragraphs": [
          "Clinically, it is more useful to ask 'What lowers the threshold?' rather than 'What triggers it?'. Mast cells respond not only to IgE but also to neuropeptides like substance P (via the MRGPRX2 receptor), complement factors C3a and C5a, cytokines from silent inflammation, stress hormones and microbial components. If the cell is under constant stimulation, a trivial impulse – such as heat, pressure, a glass of wine or an infection – is then enough to trigger the cascade.",
          "This also explains the frustrating experience many patients have: a food diary reveals no clear pattern because the pattern lies not in a specific trigger, but in the cumulative burden of various stressors on any given day."
        ]
      },
      {
        "heading": "Histamine metabolism, the microbiome and infection foci",
        "paragraphs": [
          "Histamine does not only come from mast cells. It is also ingested through food, produced by certain gut bacteria (including histidine decarboxylase-positive lactobacilli and enterobacteria), and broken down by diamine oxidase (DAO) in the intestinal epithelium as well as intracellularly by histamine N-methyltransferase (HNMT). An impaired barrier and dysbiotic flora increase the histamine load from below, while the mast cells add more from above.",
          "Equally relevant are persistent foci of infection: Helicobacter pylori, chronic sinusitis, dental foci, parasitic infections or – less frequently considered – smouldering urogenital infections. Autoimmune thyroiditis (Hashimoto's) is also found more frequently than by chance in patients with urticaria. These connections are well documented, yet often remain unaddressed in everyday clinical practice."
        ]
      },
      {
        "heading": "What this means for our support",
        "paragraphs": [
          "My work here is strictly complementary to dermatological treatment. Antihistamines or anti-IgE therapy are useful where indicated and should not be discontinued. My role lies alongside this: to raise the activation threshold again by addressing the intestinal barrier, microbiota, histamine breakdown, micronutrient status and the stress axis. Vitamin D, zinc, copper and vitamin B6 are cofactors for histamine degradation – deficiencies can be measured and corrected."
        ]
      },
      {
        "heading": "Remission is common in urticaria",
        "paragraphs": [
          "Chronic urticaria has a remarkable characteristic: it tends towards remission. A significant proportion of cases eventually come to a standstill – and this process can be supported by specifically targeting mast cell stability, histamine breakdown, the intestinal barrier and the overall inflammatory burden.",
          "It is realistic to expect fewer and milder flare-ups, a reduced need for antihistamines and longer symptom-free periods; it is not uncommon for the hives to disappear completely for months. Even after years of daily flare-ups, this turning point is possible – I have seen it often enough to say so with confidence."
        ]
      }
    ],
    "signs": [
      "Wheals that heal without scarring within 24 hours and reappear elsewhere",
      "Itching that intensifies in the evening and at night",
      "Swelling of the lips, eyelids or hands (angioedema)",
      "Flare-ups triggered by heat, pressure, alcohol, stress or infections",
      "Allergy tests that show no conclusive results",
      "Symptoms persisting for more than six weeks"
    ],
    "approach": [
      "Detailed medical history regarding the course of the condition, previous findings, medication and flare-up patterns",
      "Laboratory testing: inflammatory, thyroid and autoimmune markers, ferritin, vitamin D, zinc, copper and vitamin B6",
      "Investigation of infection foci (including Helicobacter pylori, urogenital and dental sources) and microbiological stool diagnostics",
      "Assessment of the histamine load: diet, DAO cofactors and histamine-producing flora",
      "Targeted restoration of the mucosal barrier and microbiota instead of a blanket, long-term elimination diet",
      "Phytotherapeutic and micronutrient-based mast cell stabilisation, alongside specialist medical therapy"
    ],
    "faq": [
      {
        "q": "Is chronic urticaria an allergy?",
        "a": "In most cases, no. Chronic spontaneous urticaria is primarily driven by autoimmune or autoallergic factors. This is why allergy tests often come back clear – which is not a contradiction to your symptoms, but rather the typical finding."
      },
      {
        "q": "Should I eat a low-histamine diet?",
        "a": "For a limited time, a histamine-reduced diet can lower your overall load and calm the course of the condition. However, it is not ideal as a permanent solution because it reduces the diversity of the gut flora, ultimately working against your long-term goal. Building tolerance is the real objective."
      },
      {
        "q": "Do I need to stop taking my antihistamines?",
        "a": "No. Your specialist medication is decided by your treating physician. I work complementarily on the factors that lower the activation threshold – not against your existing therapy."
      },
      {
        "q": "How long does it take to see a change?",
        "a": "Realistically, it takes several months. Mast cells, mucous membranes and the microbiota do not change in a matter of weeks. The first signs of progress are usually seen in the frequency of flare-ups, rather than in the appearance of individual wheals."
      }
    ]
  },
  "neurodermitis-psoriasis": {
    "title": "Atopic Dermatitis and Psoriasis: Two Paths, One Shared Axis",
    "shortTitle": "Atopic Dermatitis & Psoriasis",
    "metaTitle": "Atopic Dermatitis & Psoriasis | Praxis IB Vienna",
    "metaDescription": "Understanding atopic dermatitis and psoriasis: skin barrier, Th2 and Th17 axes, gut-skin axis, and microbiome. Complementary support in Vienna.",
    "keywords": [
      "atopic dermatitis microbiome",
      "psoriasis gut",
      "atopic dermatitis causes",
      "psoriasis complementary medicine",
      "gut-skin axis Vienna"
    ],
    "synonyms": [
      "atopic dermatitis",
      "psoriasis",
      "eczema in flexures",
      "itchy, scaly skin",
      "psoriatic arthritis (skin and joints affected)"
    ],
    "intro": "Atopic dermatitis and psoriasis are often lumped together because both are chronic, visible, and distressing. Immunologically, however, they are two entirely different programmes – which is precisely why it is worth taking a closer look. Anyone who has been passed from one ointment to the next for years has usually not received too little treatment, but rather too little explanation.",
    "sections": [
      {
        "heading": "Two conditions, two immunological signatures",
        "paragraphs": [
          "Atopic dermatitis is primarily a barrier disorder with Th2 dominance. A filaggrin variant is often present: the protein that cements the stratum corneum and provides natural moisturising factors is either missing or functions poorly. Water is lost, allergens and microbes penetrate more easily, and the cytokines IL-4, IL-13, and IL-31 drive inflammation and itching. The scratch reflex further destroys the barrier – a self-perpetuating cycle.",
          "Psoriasis follows the IL-23/Th17 axis. Dendritic cells activate Th17 lymphocytes, while IL-17 and IL-22 dramatically accelerate keratinocyte division: instead of taking around four weeks, the epidermis renews itself in just a few days. This results in the typical silvery scales. Psoriasis is therefore not purely a skin condition, but a systemic inflammation – with a known association to joint involvement, metabolic syndrome, and cardiovascular risk."
        ]
      },
      {
        "heading": "The gut-skin axis is not a metaphor",
        "paragraphs": [
          "Studies show abnormalities in the gut microbiota for both conditions: reduced diversity, fewer butyrate producers such as Faecalibacterium prausnitzii, and lower colonisation with Akkermansia muciniphila. Butyrate does not only nourish colonocytes; it inhibits histone deacetylases and promotes regulatory T cells – the exact cell population that dampens excessive Th2 and Th17 responses. Without this brake, a systemic, pro-inflammatory environment develops, which manifests on the skin.",
          "In psoriasis, this is compounded by lipopolysaccharides (LPS) from Gram-negative bacteria entering the circulation through a permeable barrier, further fuelling the inflammatory cascade via TLR4. The link between intestinal permeability and psoriatic activity is well documented."
        ]
      },
      {
        "heading": "The skin microbiome itself",
        "paragraphs": [
          "During an atopic dermatitis flare-up, the cutaneous flora shifts in favour of Staphylococcus aureus, which further damages the barrier and exacerbates itching via superantigens and proteases. Commensals such as Staphylococcus epidermidis and Cutibacterium species, which produce antimicrobial peptides and fatty acids, decline. A treatment that merely disinfects without enabling the commensals to return therefore falls short."
        ]
      },
      {
        "heading": "How I work with this",
        "paragraphs": [
          "Dermatological therapy – topical steroids, calcineurin inhibitors, biologics – remains the domain of your dermatologist and is indispensable for moderate to severe courses. My work addresses the underlying burden: the barrier and microbiota in the gut, inflammatory modulation via omega-3 fatty acids and polyphenols, vitamin D status, zinc, selenium, evening primrose and borage seed oils for atopic skin, assessing the stress and sleep axes, and – in the case of psoriasis – taking a realistic look at insulin resistance, alcohol, and nicotine as drivers.",
          "What I do not do: promise a cure. What I do: explain to you which axis is active in your case, what can be measured, and which factors are genuinely within your control."
        ]
      },
      {
        "heading": "Long remission periods are a realistic goal",
        "paragraphs": [
          "Atopic dermatitis and psoriasis occur in flare-ups – and that is exactly where the good news lies: flare frequency, duration, and severity can be influenced. When the barrier, microbiota, inflammatory load, and stress axis are addressed together, the calm phases lengthen significantly in the majority of cases.",
          "Many people achieve largely clear skin over longer periods, need fewer topical corticosteroids, and sleep through the night again because the itching subsides. That is not a promise of a cure, but it is a highly attainable goal."
        ]
      }
    ],
    "signs": [
      "itchy, reddened eczema in elbow creases, behind the knees, on the neck, or hands",
      "sharply defined, silvery, scaly plaques on the elbows, knees, scalp, or navel",
      "nail changes (pitting, oil drop spots) or accompanying joint pain",
      "clear flare-ups following infections, stress, or alcohol",
      "dry, sensitive skin with a constant feeling of tightness",
      "accompanying hay fever, asthma, or food reactions (atopic spectrum)"
    ],
    "approach": [
      "medical history covering the course of the condition, flare triggers, previous treatments, and family history",
      "laboratory tests: inflammatory markers, vitamin D, zinc, selenium, omega-3 index, iron status, and, if necessary, thyroid function and insulin resistance",
      "microbiological stool diagnostics including barrier and inflammatory markers (e.g. zonulin, secretory IgA, alpha-1-antitrypsin, calprotectin)",
      "targeted cultivation of butyrate-producing flora rather than indiscriminate probiotic use",
      "inflammation-modulating micronutrient and phytotherapy, tailored to the relevant immune axis",
      "close coordination with ongoing dermatological treatment"
    ],
    "faq": [
      {
        "q": "Can atopic dermatitis or psoriasis be cured via the gut?",
        "a": "No, and any claim to the contrary should make you suspicious. Both conditions have a genetic basis. What can be influenced is the inflammatory load, and thus the frequency and severity of flare-ups – and that is often crucial for quality of life."
      },
      {
        "q": "Do I need a stool analysis?",
        "a": "It is useful if there are also digestive symptoms, if antibiotics played a role in your medical history, or if the condition remains resistant to therapy despite consistent skincare. I decide this based on your medical history, not a fixed protocol."
      },
      {
        "q": "Do I need to eliminate foods?",
        "a": "Blanket elimination diets are rarely helpful for skin conditions and can be potentially harmful for children. A more sensible approach is an anti-inflammatory diet that nourishes the microbiota – meaning more diversity, not less."
      },
      {
        "q": "I have psoriasis and joint pain. Are they connected?",
        "a": "Possibly, yes. Up to a third of those affected develop psoriatic arthritis. This requires rheumatological investigation – and the earlier, the better, because early treatment can prevent joint damage."
      }
    ]
  },
  "rheumatische-beschwerden-arthritis-arthrose-fibromyalgie": {
    "title": "Rheumatic complaints: reactive arthritis, osteoarthritis and fibromyalgia",
    "shortTitle": "Rheumatism, osteoarthritis & fibromyalgia",
    "metaTitle": "Reactive Arthritis, Osteoarthritis & Fibromyalgia in Vienna",
    "metaDescription": "Understanding joint and muscle pain: reactive arthritis, inflammatory osteoarthritis, fibromyalgia and the microbiome. Holistic support in Vienna.",
    "keywords": [
      "reactive arthritis after infection",
      "osteoarthritis inflammation diet",
      "fibromyalgia causes",
      "joint pain microbiome",
      "rheumatism complementary medicine Vienna"
    ],
    "synonyms": [
      "joint pain without rheumatoid factor",
      "arthritis after intestinal or urinary tract infection",
      "joint wear and tear",
      "widespread body pain",
      "muscle pain and tender points",
      "morning stiffness"
    ],
    "intro": "Hardly anyone with joint or widespread body pain comes to my practice as their first port of call. Usually, there are years of history, multiple medical specialties, numerous test results – and the recurring phrase that you simply have to learn to live with it. This phrase is rarely entirely true. Even if a condition cannot be cured, the inflammatory burden driving it can be influenced to a surprising degree.",
    "sections": [
      {
        "heading": "Reactive arthritis: the infection is gone, but the joint pain remains",
        "paragraphs": [
          "Reactive arthritis typically develops one to four weeks after a gastrointestinal or urogenital infection – classically following Chlamydia trachomatis, Yersinia, Salmonella, Campylobacter or Shigella. It usually affects a few large joints of the lower extremities, often asymmetrically, and is frequently accompanied by enthesitis (inflammation where tendons attach to bone) or dactylitis. The risk is significantly higher in HLA-B27 positive individuals.",
          "The pathophysiology is thought to involve persistent microbial antigens in the joint and molecular mimicry: bacterial structures resemble the body's own tissues closely enough that the immune system inadvertently attacks them. This explains why failing to detect a pathogen in the joint fluid does not rule out the diagnosis – and why asking about previous infections, even months earlier, is so clinically valuable."
        ]
      },
      {
        "heading": "Osteoarthritis is more than just wear and tear",
        "paragraphs": [
          "The concept of a mechanically worn-out joint is outdated. The modern perspective views osteoarthritis as a low-grade inflammatory process affecting the entire joint organ: under mechanical stress, chondrocytes release IL-1β, TNF-α, and matrix metalloproteinases; the synovial membrane reacts with mild synovitis, and the subchondral bone undergoes remodelling. Cartilage breakdown is therefore not just a result of pressure, but of biochemical activity.",
          "This is precisely where systemic inflammatory burden becomes relevant. Visceral fat tissue produces adipokines like leptin, which catabolically stimulate chondrocytes; insulin resistance exacerbates this process. This is why osteoarthritis also affects non-weight-bearing joints such as those in the fingers – a purely mechanical model could never explain this."
        ]
      },
      {
        "heading": "Fibromyalgia: central sensitisation with a peripheral context",
        "paragraphs": [
          "Fibromyalgia is not merely a diagnosis of exclusion when nothing else can be found; it is an independent disorder of pain processing. It is characterised by central sensitisation with elevated substance P levels in the cerebrospinal fluid, weakened descending pain inhibition, and altered activity of the hypothalamic-pituitary-adrenal axis. Imaging studies show tangible differences in central nervous system pain processing. The pain is not imagined – it is processed differently.",
          "Alongside this, there are frequently relevant contributing factors: subclinical thyroid dysfunction and Hashimoto's thyroiditis, iron and vitamin D deficiencies, magnesium and B-vitamin deficits, mitochondrial exhaustion in the muscles, disrupted sleep architecture with a lack of deep sleep – and, as described in numerous studies, dysbiosis characterised by reduced diversity and altered production of short-chain fatty acids and neurotransmitter precursors."
        ]
      },
      {
        "heading": "The common denominator: the gut, the barrier and the immune system",
        "paragraphs": [
          "Around 70 per cent of our immune cells reside in the gut-associated lymphoid tissue. A permeable mucous membrane allows bacterial components – particularly LPS – to enter the bloodstream, where they sustain a persistent, low-level immune activation via TLR4. This 'silent inflammation' lowers the pain threshold, keeps synovial tissue on high alert and places significant strain on the mitochondria.",
          "In stubborn, treatment-resistant cases with a history of infection, I also employ modern microbiological diagnostics – qPCR and Next-Generation Sequencing via MicroGenDX – because culture-based methods regularly miss biofilm-associated and difficult-to-culture pathogens. This is not necessary for everyone. However, for those in whom nothing has ever been found, it is sometimes exactly the missing piece of the puzzle."
        ]
      },
      {
        "heading": "What you can expect from me – and what you cannot",
        "paragraphs": [
          "I do not replace a rheumatologist. Inflammatory rheumatic diseases must be diagnosed by a specialist and, where indicated, treated with immunomodulators; an established baseline therapy should not be discontinued. What I provide is work on the underlying burden: modulating inflammation, assessing micronutrient and mitochondrial status, addressing the microbiota and intestinal barrier, identifying foci of infection, and supporting sleep and the stress axis.",
          "And I take the time to go through your medical reports from recent years with you. Very often, there is more in those reports than has ever been explained to you."
        ]
      },
      {
        "heading": "What can be changed – and that is a great deal",
        "paragraphs": [
          "Even if a rheumatic disease remains: the inflammatory burden driving it can be influenced to a surprising degree. Less morning stiffness, reduced pain intensity, improved mobility, greater resilience and more restorative sleep are achievable for the majority of patients.",
          "With reactive arthritis, complete recovery is common; with fibromyalgia, many sufferers report phases after a few months where the pain recedes into the background. A life that is once again planned by you and not by your joints – that is the benchmark, and it is entirely realistic."
        ]
      }
    ],
    "signs": [
      "Joint swelling or pain that began weeks after an intestinal or urinary tract infection",
      "Morning stiffness lasting longer than 30 minutes",
      "Pain at tendon insertions, the heel, or individually swollen fingers or toes",
      "Load-dependent joint pain, with initial pain upon moving after a period of rest",
      "Widespread muscle and soft tissue pain lasting more than three months",
      "Unrefreshing sleep, poor concentration and increased sensitivity to stimuli"
    ],
    "approach": [
      "A structured review of all previous medical reports – rheumatological, orthopaedic, and laboratory",
      "Laboratory tests: inflammatory markers, iron and ferritin status, vitamin D, B12, folic acid, magnesium, thyroid function including antibodies, and homocysteine",
      "Investigation of past infections and – in stubborn cases – qPCR/NGS diagnostics via MicroGenDX",
      "Microbiological stool testing with barrier and inflammatory markers",
      "An inflammation-modulating nutritional, micronutrient, and phytotherapy strategy (including omega-3, pharmaceutical-grade curcumin preparations, frankincense, and polyphenols)",
      "Support for mitochondrial energy production, as well as the sleep and stress axes",
      "Close coordination with rheumatologists, orthopaedic specialists, and your GP"
    ],
    "faq": [
      {
        "q": "My rheumatism markers are normal – why am I still in pain?",
        "a": "Normal rheumatoid factors, CCP antibodies or an unremarkable CRP do not rule out seronegative arthritis, reactive arthritis, osteoarthritis, or fibromyalgia. These markers reflect specific disease mechanisms, not the presence of pain."
      },
      {
        "q": "Can a change in diet really make a difference with osteoarthritis?",
        "a": "Cartilage does not grow back. However, the inflammatory activity around the joint can be influenced, and it is precisely this that determines a significant portion of the pain and disease progression. An improvement in symptoms and function is realistic, though reversing the structural damage is not."
      },
      {
        "q": "Is fibromyalgia psychosomatic?",
        "a": "It is a neurobiologically tangible disorder of pain processing. Psychological stress can influence its course and your pain threshold – as is the case with almost any chronic illness – but it is not the root cause. This distinction is important to me, because many sufferers have never been told this."
      },
      {
        "q": "Do I need to change my rheumatology treatment?",
        "a": "No. I work in a complementary capacity and coordinate with your specialist. I do not make changes to baseline therapies or biologics."
      },
      {
        "q": "How long does this kind of support take?",
        "a": "Following the initial consultation, which involves taking your medical history and discussing your test results, there is usually a diagnostic phase; after that, we work in stages. In my experience, robust changes become apparent over three to six months, rather than weeks."
      }
    ]
  },
  "ced-morbus-crohn-colitis-ulcerosa": {
    "title": "Inflammatory Bowel Disease: Supporting Crohn's Disease and Ulcerative Colitis",
    "shortTitle": "Crohn's Disease & Ulcerative Colitis",
    "metaTitle": "Crohn's & Ulcerative Colitis Support | Praxis IB Vienna",
    "metaDescription": "Understanding IBD: barrier function, microbiota, micronutrients and inflammation modulation – complementary to gastroenterology treatments in Vienna.",
    "keywords": [
      "Crohn's disease Vienna",
      "ulcerative colitis complementary medicine",
      "IBD microbiome",
      "supporting gut remission",
      "inflammatory bowel disease diet"
    ],
    "synonyms": [
      "IBD",
      "Crohn's disease",
      "Ulcerative colitis",
      "flare-up despite therapy",
      "bloody diarrhoea",
      "elevated calprotectin"
    ],
    "intro": "Crohn's disease and ulcerative colitis are, and must remain, conditions managed by a gastroenterologist. My contribution begins where immunosuppression takes effect, but quality of life has not yet returned: focusing on the intestinal barrier, the microbiota, nutrient supply, and the underlying inflammatory burden that continues between flare-ups.",
    "sections": [
      {
        "heading": "What defines IBD at a cellular level",
        "paragraphs": [
          "Both conditions are driven by a misguided immune response of the intestinal mucosa to the body's own microbiota – occurring in individuals with a genetic predisposition (such as NOD2 variants in Crohn's disease) and influenced by environmental factors. Crohn's disease features transmural, segmental inflammation with a Th1/Th17 predominance and can affect the entire digestive tract; ulcerative colitis remains confined to the mucosa and submucosa of the colon, spreading continuously from the rectum in a proximal direction.",
          "What they share is an impaired intestinal barrier: altered tight junction proteins, a thinner mucus layer, and reduced antimicrobial peptides from Paneth cells. This allows bacterial components to reach immune cells that they would never encounter in an intact mucosa – thus, the inflammation becomes self-sustaining."
        ]
      },
      {
        "heading": "The microbiota is not a side issue in IBD",
        "paragraphs": [
          "A characteristic feature is reduced diversity alongside a loss of butyrate-producing bacteria, most notably Faecalibacterium prausnitzii, whose decline has been associated in studies with a higher relapse rate following remission. At the same time, Proteobacteria increase, particularly adherent-invasive Escherichia coli in Crohn's disease.",
          "Butyrate is the primary energy source for colonocytes and also exerts immunoregulatory effects by promoting regulatory T cells. When it is lacking, the mucosa loses both its energy supply and its braking mechanism. This explains why microbiota-oriented work in IBD is not merely a wellness trend, but targets a genuine, underlying mechanism."
        ]
      },
      {
        "heading": "Deficiencies that influence the disease course",
        "paragraphs": [
          "Iron deficiency due to chronic blood loss and inflammation-induced elevated hepcidin; vitamin B12 deficiency in cases of terminal ileum involvement or resection; vitamin D deficits with immunological relevance; and shortages of zinc, selenium, and folic acid – some disease-related, others medication-associated – are the rule rather than the exception in IBD.",
          "These deficiencies are measurable and correctable. They often explain the exact type of exhaustion, susceptibility to infection, and impaired wound healing that persists even during a solid remission, yet finds no dedicated space on a standard lab report."
        ]
      },
      {
        "heading": "Complementary – expressly not alternative",
        "paragraphs": [
          "Mesalazine, corticosteroids, immunomodulators, and biologics are neither adjusted nor discontinued by me under any circumstances. My area of expertise is the support provided between routine check-ups: formulating dietary strategies during and outside of flare-ups, correcting micronutrient levels based on lab results, prescribing well-tolerated, pharmaceutical-grade, inflammation-modulating phytotherapeutics, rebuilding the intestinal barrier during stable phases, and addressing the stress axis, whose impact on flare-up frequency is well documented."
        ]
      },
      {
        "heading": "Remission is the goal – and often attainable",
        "paragraphs": [
          "Today, inflammatory bowel diseases are more treatable than ever before. With modern gastroenterological therapy, many individuals achieve sustained remission; complementary work on nutrient supply, barrier function, and the underlying inflammatory burden can stabilise and prolong these phases.",
          "What I see time and again: people who remained exhausted despite being in remission regain their energy once iron, B12, vitamin D, and their microbiota are balanced again. An active, predictable life with IBD is not just a consolation prize, but a realistic goal for the majority of those affected."
        ]
      }
    ],
    "signs": [
      "recurrent diarrhoea, sometimes with blood or mucus",
      "cramping abdominal pain, often in the lower right abdomen",
      "unintentional weight loss and persistent exhaustion",
      "elevated faecal calprotectin despite a subjectively quiet phase",
      "accompanying symptoms affecting the joints, skin, or eyes",
      "recurring iron or vitamin B12 deficiency"
    ],
    "approach": [
      "Review of all gastroenterological reports, including endoscopy, histology, and calprotectin progression",
      "Laboratory tests: iron profile with ferritin and transferrin saturation, vitamin B12, folic acid, vitamin D, zinc, selenium, and inflammatory markers",
      "Microbiological stool diagnostics, including barrier, digestive, and inflammatory parameters",
      "Phase-appropriate dietary strategy: relieving the gut during a flare-up, promoting diversity during remission",
      "Targeted micronutrient therapy and well-tolerated, pharmaceutical-grade phytotherapeutics",
      "Focus on sleep, the stress axis, and regeneration as recognised factors influencing flare-up frequency",
      "Ongoing coordination with your treating gastroenterologist"
    ],
    "faq": [
      {
        "q": "Can I replace my medication with naturopathy?",
        "a": "No, and I would strongly advise against it. IBD without effective inflammation control risks complications that cannot be reversed later on. Complementary medicine works adjunctively here – focusing on nutrient supply, the intestinal barrier, and the underlying inflammatory burden."
      },
      {
        "q": "Is there a one-size-fits-all diet?",
        "a": "No. During an acute flare-up, relieving the gut makes sense, whereas during remission, the exact opposite is true: variety and dietary fibre, as much as you can individually tolerate. Long-term restriction depletes the microbiota and worsens the baseline situation over time."
      },
      {
        "q": "Are probiotics useful for IBD?",
        "a": "It requires a nuanced approach. In ulcerative colitis, there is solid data supporting the use of specific strains and multi-species preparations for maintaining remission; in Crohn's disease, the evidence is significantly weaker. Blanket recommendations are entirely out of place here – the selection must be guided by your specific diagnosis, test results, and disease phase."
      }
    ]
  },
  "divertikulitis": {
    "title": "Diverticula and Diverticulitis: Understanding and Preventing Relapses",
    "shortTitle": "Diverticulitis",
    "metaTitle": "Diverticulitis: Prevent Relapses | Praxis IB Vienna",
    "metaDescription": "Diverticulosis and recurrent diverticulitis: microbiota, dietary fibre, inflammation modulation, and recovery after antibiotics. Expert support in Vienna.",
    "keywords": [
      "prevent diverticulitis relapse",
      "diverticulosis diet",
      "diverticulitis microbiome",
      "gut recovery after antibiotics",
      "diverticulitis Vienna"
    ],
    "synonyms": [
      "diverticula in the bowel",
      "recurrent diverticulitis",
      "left lower quadrant pain",
      "bowel inflammation from diverticula",
      "SUDD – symptomatic uncomplicated diverticular disease"
    ],
    "intro": "A significant proportion of the population develops diverticula from their fifties onwards, and most remain symptom-free for life. The situation requires attention when things do not stay quiet: in cases of recurrent inflammation and persistent symptoms that fail to resolve between flare-ups.",
    "sections": [
      {
        "heading": "How diverticula develop – and why they become inflamed",
        "paragraphs": [
          "Diverticula are outpouchings of the mucosa through weak areas in the muscular wall of the bowel. They most commonly occur in the sigmoid colon, where internal pressure is highest. Their development is promoted by a low-fibre diet, age-related changes in collagen structure, and impaired motility.",
          "The traditional view that a trapped seed or kernel triggers the inflammation is now considered outdated. Instead, current models describe chronic, low-grade inflammation of the area surrounding the diverticula, local dysbiosis, and altered enteric nerve function. This also explains why nuts and seeds are no longer strictly prohibited – on the contrary, cohort data suggest they may actually be associated with a lower risk."
        ]
      },
      {
        "heading": "Why symptoms often persist after antibiotic therapy",
        "paragraphs": [
          "Acute diverticulitis is treated in accordance with clinical guidelines; complicated cases require medical and sometimes surgical intervention. What frequently remains afterwards is a bowel that has suffered a double hit: both the inflammation itself and the antibiotic therapy have depleted the microbiota, particularly butyrate-producing genera.",
          "Without butyrate, the mucosa lacks energy and immunological regulation. It is precisely in this vulnerable state that typical residual symptoms arise – pressure, bloating, and irregular bowel movements – and this also accounts for part of the relapse risk. Rebuilding the gut after a flare-up is therefore not an optional extra."
        ]
      },
      {
        "heading": "Dietary fibre: the right amount at the right time",
        "paragraphs": [
          "During an acute flare-up, resting the bowel is indicated. Long-term, however, the opposite is protective: a high-fibre diet reduces intraluminal pressure, shortens transit time, and nourishes the exact bacteria whose metabolites stabilise the mucosa. The transition between these two phases must be gradual; otherwise, the right measure is applied at the wrong time and mistakenly perceived as intolerable."
        ]
      },
      {
        "heading": "The outlook is positive",
        "paragraphs": [
          "The vast majority of people with diverticula remain symptom-free – and even after multiple flare-ups, the risk of relapse can be effectively reduced. Fibre diversity, a stable microbiota, physical activity, and the cautious use of NSAIDs are all factors with a proven protective effect, and all are within your control.",
          "Following a structured gut rehabilitation programme, many patients report that even stubborn residual symptoms between flare-ups disappear. A calm, reliable bowel is an achievable goal in most cases."
        ]
      }
    ],
    "signs": [
      "recurrent pain in the left lower abdomen",
      "a sensation of pressure and fullness that briefly subsides after a bowel movement",
      "altered bowel habits with a tendency towards constipation",
      "symptoms that persist even between inflammatory flare-ups",
      "more than one episode of diverticulitis treated with antibiotics in recent years"
    ],
    "approach": [
      "Review of colonoscopy, CT, and laboratory results, as well as the history of previous flare-ups",
      "Microbiological stool testing, including butyrate producers, barrier markers, and inflammatory markers",
      "Structured gut rehabilitation after antibiotic therapy, rather than relying on random off-the-shelf products",
      "Gradual, tolerance-guided increase in dietary fibre diversity",
      "Inflammation-modulating phytotherapy and micronutrients based on test results",
      "Clear boundaries: acute or complicated cases require immediate medical evaluation"
    ],
    "faq": [
      {
        "q": "Can I really eat nuts and seeds?",
        "a": "According to current data, yes. Large prospective cohorts have found no increased risk, and in some cases, even a reduced risk. The old prohibition was based on a plausible-sounding assumption that has not been confirmed."
      },
      {
        "q": "When should I seek immediate medical attention?",
        "a": "If you experience severe or rapidly worsening pain, fever, vomiting, a rigid abdomen, or blood in your stool. These are warning signs of complicated diverticulitis and are not appropriate for complementary medical support."
      },
      {
        "q": "Can I prevent further flare-ups?",
        "a": "There are no guarantees. However, a high-fibre diet, a stable microbiota, physical activity, weight management, and the cautious use of NSAIDs are all factors described as having a risk-reducing effect – and all of these are within your control."
      }
    ]
  },
  "prostatabeschwerden-cpps": {
    "title": "Prostate Complaints and Chronic Pelvic Pain Syndrome in Men",
    "shortTitle": "Prostate Complaints & CPPS",
    "metaTitle": "Prostatitis & Chronic Pelvic Pain | Praxis IB Vienna",
    "metaDescription": "Chronic prostatitis and CPPS: biofilms, qPCR and NGS diagnostics, microbiome and pelvic floor – complementary medical support for men in Vienna.",
    "keywords": [
      "chronic prostatitis Vienna",
      "CPPS chronic pelvic pain syndrome",
      "culture-negative prostatitis",
      "prostate biofilm qPCR",
      "bladder complaints men complementary"
    ],
    "synonyms": [
      "chronic prostatitis",
      "CPPS – chronic pelvic pain syndrome",
      "pelvic pain in men",
      "burning on urination with clear test results",
      "frequent urge to urinate despite negative cultures",
      "perineal pain or pain when sitting"
    ],
    "intro": "Men with chronic prostate and pelvic complaints often have a long medical history: multiple courses of antibiotics, unremarkable cultures, and being told that 'nothing can be found'. The fact that nothing was found does not mean nothing is there – initially, it merely means the method used did not reveal anything.",
    "sections": [
      {
        "heading": "Four Categories, One Common Ordeal",
        "paragraphs": [
          "The NIH classification distinguishes between acute bacterial prostatitis (I), chronic bacterial prostatitis (II), chronic pelvic pain syndrome with or without signs of inflammation (IIIa/IIIb), and asymptomatic inflammatory prostatitis (IV). The vast majority of patients fall into category III – the very group for whom there is no simple antibiotic answer.",
          "Clinically, symptoms overlap, including pain in the perineum, testicles, tip of the penis or lower abdomen, voiding difficulties, pain during or after ejaculation, and a pronounced intolerance to sitting. The burden is significant and routinely underestimated."
        ]
      },
      {
        "heading": "Why Cultures Do Not Show Enough",
        "paragraphs": [
          "Conventional urine and secretion cultures only capture what can be grown under standard conditions. Biofilm-associated, intracellular, and fastidious pathogens systematically evade this process – and these are precisely the ones relevant to chronic, relapsing urogenital conditions. Within a biofilm, metabolic activity drops, the matrix hinders antibiotic penetration, and tolerance to active agents increases manifold.",
          "This is where I employ modern microbiological diagnostics: qPCR and Next-Generation Sequencing via MicroGenDX detect microbial DNA independently of cultivability and additionally provide information on resistance genes. This does not replace a urological assessment – it complements it exactly where it routinely reaches its limits."
        ]
      },
      {
        "heading": "Pelvic Floor, Nervous System and Microbiome",
        "paragraphs": [
          "In cases of CPPS without pathogen detection, a myofascial component is often predominant: chronically increased pelvic floor muscle tone with trigger points, exacerbated by pain avoidance, sitting posture, and tension. This is accompanied by central sensitisation, which maintains the pain even after the original stimulus has subsided.",
          "The gut-bladder axis also plays a role: an impaired barrier and an altered microbiome in the urogenital tract sustain a low-grade immune activation. For this reason, I never treat just one organ here; instead, I address the barrier, microbiota, inflammatory load, pelvic floor tone, and stress axis together."
        ]
      },
      {
        "heading": "Even After a Long History, Treatment Is Worthwhile",
        "paragraphs": [
          "CPPS is considered difficult – primarily because it is usually only treated from one angle. If microbial, muscular, and neurological components are addressed simultaneously, pain, urinary urgency, and sitting intolerance improve significantly in the majority of cases.",
          "Many men achieve prolonged symptom-free periods, manage without further courses of antibiotics, and return to sports, work, and a fulfilling sex life without constant reservations. Even after years, this progress is possible; the duration of the medical history says little about the prospects for recovery."
        ]
      }
    ],
    "signs": [
      "Pain or pressure in the perineum, testicles, lower abdomen, or the tip of the penis",
      "Frequent urge to urinate, a weak stream, or a feeling of incomplete emptying",
      "Burning on urination despite repeatedly negative cultures",
      "Discomfort during or after ejaculation",
      "Significant worsening of symptoms after prolonged sitting",
      "Multiple courses of antibiotics with only temporary improvement"
    ],
    "approach": [
      "Assuming prior urological assessment – review of PSA, ultrasound, residual urine, and culture results",
      "Advanced microbiological diagnostics using qPCR and NGS via MicroGenDX for recurrent cases",
      "Microbiological stool diagnostics to assess barrier function and inflammatory load",
      "Biofilm-disrupting strategies using pharmaceutical-grade phytotherapeutics, tailored to your test results",
      "Micronutrient and inflammation modulation, including zinc, vitamin D, omega-3, and polyphenols",
      "Recommendation of pelvic floor physiotherapy for myofascial components",
      "Working on the stress axis and pain processing as an integral part of the treatment, not an afterthought"
    ],
    "faq": [
      {
        "q": "I have already had several courses of antibiotics – why does it keep coming back?",
        "a": "Because a significant proportion of chronic urogenital cases are biofilm-associated. Antibiotics target planktonic bacteria well, but are far less effective against bacteria within a biofilm matrix. Once the antibiotics are stopped, the biofilm can reactivate – clinically, this appears as a new infection."
      },
      {
        "q": "What exactly do qPCR and NGS diagnostics achieve?",
        "a": "They detect microbial DNA regardless of whether the pathogen can be cultured, quantify the species found, and identify resistance genes. For patients who have been told for years that 'nothing can be found', this is often the first concrete clue."
      },
      {
        "q": "Does this replace the urologist?",
        "a": "No. PSA monitoring, the exclusion of structural causes, and tumour screening must strictly remain in urological hands. My work is complementary, and I am happy to coordinate with your treating practice."
      },
      {
        "q": "Is CPPS psychological?",
        "a": "No. It is a multifactorial condition involving microbial, muscular, and neurological components. However, persistent pain alters pain processing and psychological resilience – this is a consequence, not a cause."
      }
    ]
  },
  "biofilme-antibiotikaresistenz": {
    "title": "Biofilms, Pathogens and Antibiotic Resistance: Modern Microbiological Diagnostics",
    "shortTitle": "Biofilms & Antibiotic Resistance",
    "metaTitle": "Biofilms & Resistance: qPCR & NGS | Praxis IB Vienna",
    "metaDescription": "Persistent infections despite antibiotics: understand biofilms, detect resistance genes, strengthen microbiota. qPCR & NGS via MicroGenDX in Vienna.",
    "keywords": [
      "biofilm infection diagnostics",
      "qPCR NGS pathogen detection Vienna",
      "MicroGenDX Austria",
      "antibiotic resistance microbiome",
      "undiagnosed chronic infection"
    ],
    "synonyms": [
      "biofilms in the body",
      "recurring infections",
      "negative culture despite symptoms",
      "multidrug-resistant bacteria",
      "antibiotics no longer working"
    ],
    "intro": "When an infection returns after every course of antibiotics, it is rarely due to a lack of discipline and rarely imaginary. Often, it is down to a life form that standard diagnostics barely detect: the biofilm. This perspective fundamentally changes both diagnostics and our clinical approach – and for the first time, it offers many people a coherent explanation.",
    "sections": [
      {
        "heading": "What a biofilm is and why it creates such resilience",
        "paragraphs": [
          "The vast majority of bacteria do not live free-floating, but in organised communities, embedded in a self-produced matrix of polysaccharides, proteins and extracellular DNA. This matrix acts as a diffusion barrier, buffers oxygen and nutrient gradients, and harbours so-called persister cells with a severely down-regulated metabolism.",
          "Since most antibiotics target active metabolic processes, such cells are up to a thousand times more tolerant to them than the same species in planktonic form – without the need for classic genetic resistance. Through quorum sensing, bacteria also coordinate their behaviour and exchange resistance genes via horizontal gene transfer."
        ]
      },
      {
        "heading": "Why cultures routinely come back negative",
        "paragraphs": [
          "Culture-based methods only capture what grows under laboratory conditions. Anaerobes, intracellular pathogens, slow-growing or biofilm-bound bacteria remain invisible. A negative result therefore simply means that nothing was detectable using this method – not that nothing is there.",
          "In contrast, qPCR and Next-Generation Sequencing (NGS) work independently of cultures at the DNA level. They identify and quantify species, map polymicrobial communities and also detect resistance genes. For these diagnostics, I work with MicroGenDX. The results are always evaluated within a clinical context – DNA detection alone is not an indication for treatment."
        ]
      },
      {
        "heading": "Resistance development: what you can actually influence",
        "paragraphs": [
          "Antibiotic resistance is a global problem, yet also a highly individual one. Every treatment alters your own microbiota and selects for resistant strains, which remain as a reservoir. A resilient, diverse microbiome is therefore not only relevant for digestion, but also acts as an active protective factor: it occupies ecological niches, competes for nutrients and produces antimicrobial substances.",
          "In practice, this means: targeted rather than reflexive antibiotic use, consistent restoration afterwards and, where justifiable, biofilm-disrupting strategies using standardised pharmaceutical-grade phytotherapeutics. Antibiotics remain indispensable – but they should only be used when genuinely needed."
        ]
      },
      {
        "heading": "Who this approach is designed for",
        "paragraphs": [
          "For people with recurrent urinary tract, respiratory tract, skin, or dental and jaw infections; those with chronic urogenital symptoms without detected pathogens; individuals with persistent symptoms following past infections; or those with a history of multiple antibiotic cycles. Not everyone requires these diagnostics. But those who do usually recognise themselves in one sentence: 'They never found anything.'"
        ]
      },
      {
        "heading": "Why this perspective offers hope",
        "paragraphs": [
          "Once you understand why antibiotics alone have not been enough, you have already taken the crucial first step. With a clear diagnosis and a strategy that simultaneously targets the biofilm matrix, pathogen load and your own microbiota, many people finally break a cycle that has seemed unchangeable for years.",
          "What is realistic? Longer infection-free intervals, significantly less frequent antibiotic use, and an immune system that can once again do its job. The body is astonishingly good at restoring order once given the right conditions."
        ]
      }
    ],
    "signs": [
      "Infections that return within a few weeks of every antibiotic course",
      "Persistent symptoms despite repeatedly negative cultures",
      "Multiple courses of antibiotics within a single year",
      "Symptoms that have never entirely disappeared following an infection",
      "A history of multidrug-resistant pathogens",
      "Increasing susceptibility to infections following prolonged treatments"
    ],
    "approach": [
      "Detailed infection and treatment history, including all previous antibiotic cycles",
      "Culture-independent pathogen diagnostics using qPCR and NGS via MicroGenDX, including resistance genes",
      "Microbiological stool diagnostics to assess diversity, gut barrier function and inflammation",
      "Interpretation of results always within the clinical context, rather than relying solely on lab reports",
      "Biofilm-disrupting pharmaceutical-grade phytotherapy, tailored to the specific pathogen and tolerability",
      "Targeted restoration of the microbiota following antibiotic therapy",
      "Feedback and coordination with your treating medical practice"
    ],
    "faq": [
      {
        "q": "Does qPCR/NGS replace an antibiogram?",
        "a": "No. The methods complement each other: cultures and antibiograms show the sensitivities of culturable pathogens, while qPCR and NGS reveal the complete microbial picture, including non-culturable species and resistance genes. Treatment decisions are always made in a medical context."
      },
      {
        "q": "Can biofilms be broken down?",
        "a": "Complete eradication is not a realistic promise. However, there are substances and strategies that destabilise the matrix, disrupt quorum sensing and increase sensitivity to the primary treatment. The goal is to return control to the body, not to promise a miracle."
      },
      {
        "q": "Does every positive DNA result lead to treatment?",
        "a": "No. The human body is naturally colonised by microbes, and that is a good thing. What matters is the combination of symptoms, quantity, species and clinical course. Anything else would be treating a lab result rather than a person."
      },
      {
        "q": "How long does this type of support take?",
        "a": "Following the initial medical history and diagnostics, we work in stages with regular follow-ups. For chronic, biofilm-associated conditions, three to six months is a realistic timeframe to see robust changes."
      }
    ]
  },
  "reaktive-arthritis": {
    "title": "Reactive Arthritis: When Joints Inflame After an Infection",
    "shortTitle": "Reactive arthritis",
    "metaTitle": "Reactive Arthritis After Infection: Causes & Support | PRAXIS IB Vienna",
    "metaDescription": "Reactive arthritis after a gut or urinary tract infection: understanding mucosa, HLA-B27 and the immune response – complementary support in Vienna.",
    "keywords": [
      "reactive arthritis Vienna",
      "joint pain after infection",
      "Reiter's syndrome",
      "HLA-B27 arthritis",
      "arthritis after gut infection"
    ],
    "synonyms": [
      "arthritis after a gastrointestinal infection",
      "joint pain after a urinary tract infection",
      "Reiter's syndrome (historical term)",
      "post-infectious arthritis",
      "swollen knee after infection"
    ],
    "intro": "First the infection, then – seemingly out of nowhere – a swollen knee, a painful heel, a stiff lower back. Reactive arthritis is not a joint infection but the response of an immune system that cannot return to rest after an infection elsewhere.",
    "sections": [
      {
        "heading": "The time gap is typical",
        "paragraphs": [
          "One to four weeks usually pass between the triggering infection and the first joint symptoms. It is precisely this gap that causes the connection to be missed – the diarrhoea or urinary tract infection is long forgotten by the time the joint swells.",
          "The lower limb is predominantly affected, asymmetrically and in only a few joints. Tendon insertion pain, sacroiliac symptoms, eye irritation or skin and mucosal changes may accompany it."
        ]
      },
      {
        "heading": "Considering the point of entry",
        "paragraphs": [
          "The real site of the problem is rarely the joint but the mucosa the pathogen came through: gut or urogenital tract. Its barrier, microbiota and immune status co-determine how long the immune system remains on alert.",
          "I therefore look at both together – the joint process within a rheumatological framework and the state of the mucosa that let the trigger pass."
        ]
      },
      {
        "heading": "What conventional medicine must rule out",
        "paragraphs": [
          "Septic arthritis, gout, Lyme disease and other inflammatory rheumatic conditions must be excluded. That belongs in medical hands; my work runs alongside and never replaces this assessment."
        ]
      }
    ],
    "signs": [
      "Joint swelling one to four weeks after a gut or urinary tract infection",
      "Asymmetric involvement of few joints, usually knee, ankle or toes",
      "Heel pain or pain at tendon insertions (enthesitis)",
      "Low back pain with morning stiffness lasting over 30 minutes",
      "Accompanying eye redness, skin changes or urethral irritation"
    ],
    "approach": [
      "Complete infection and course history, including previous treatments",
      "Review of rheumatological findings, inflammatory markers and HLA-B27 status",
      "Microbiological diagnostics of the point of entry in gut or urogenital symptoms",
      "Assessment of barrier, microbiota and inflammatory status over time",
      "Coordinated complementary support alongside rheumatological treatment",
      "Follow-ups to identify chronic courses early"
    ],
    "faq": [
      {
        "q": "Is reactive arthritis contagious?",
        "a": "No. The joint itself is not infected. At most the triggering infection was contagious, and by the time joint symptoms appear it has usually long resolved."
      },
      {
        "q": "Does it go away?",
        "a": "In many cases yes – the course is often self-limiting over weeks to months. A proportion becomes chronic, which is why follow-ups make sense."
      },
      {
        "q": "What does a positive HLA-B27 result mean?",
        "a": "It describes a genetic predisposition, not a diagnosis. Many people with HLA-B27 never fall ill. What matters is the overall picture of symptoms, course and findings."
      },
      {
        "q": "What can complementary medicine usefully add?",
        "a": "A look at the mucosa the trigger came through: barrier, microbiota and inflammatory status. Rheumatological treatment remains leading and unaffected."
      }
    ]
  }
};

export const localizeComplaint = (c: Complaint, lang: string): Complaint => {
  const en = lang === "en" ? complaintsEn[c.slug] : undefined;
  if (!en) return c;
  const deep = deepSections[c.slug]?.en;
  const merged: Complaint = { ...c, ...en };
  return deep ? { ...merged, sections: [...en.sections, ...deep] } : merged;
};


export const localizedComplaints = (lang: string): Complaint[] =>
  complaints.map((c) => localizeComplaint(c, lang));
